<template>
  <div class="help-center-root">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; padding: 6px 12px; text-align: center; flex-shrink: 0;">
      <span style="font-size: 26px; font-weight: bold">Help Center</span><br/>
      <div style="font-style: italic; font-size: 16px; padding-bottom: 8px;">
        Guidance customized for your role, tenant, and features
      </div>
      <div class="help-tabs">
        <button
          class="help-tab"
          :class="{ active: activeTab === 'articles' }"
          @click="switchTab('articles')"
        >Help Articles</button>
        <button
          class="help-tab"
          :class="{ active: activeTab === 'company' }"
          @click="switchTab('company')"
        >Company Documents</button>
      </div>
      <div v-if="activeTab === 'articles'" class="help-search" style="justify-content: center; margin-top: 6px;">
        <b-form-input
          v-model="searchText"
          placeholder="Search articles..."
          size="sm"
          @keyup.enter="loadArticles"
        ></b-form-input>
        <b-button size="sm" variant="primary" @click="loadArticles" :disabled="loadingArticles">
          {{ loadingArticles ? '...' : 'Search' }}
        </b-button>
      </div>
    </div>

    <!-- HELP ARTICLES TAB -->
    <div v-if="activeTab === 'articles'" class="help-body">
      <div class="help-sidebar">
        <div v-if="activeSearch" class="help-search-indicator">
          <span>Results for "<strong>{{ activeSearch }}</strong>" ({{ articles.length }})</span>
          <button class="help-search-clear" @click="clearSearch">&times;</button>
        </div>
        <div v-if="loadingArticles" class="help-empty-state">
          Loading articles...
        </div>
        <div v-else-if="articles.length === 0" class="help-empty-state">
          No articles found for your context.
        </div>
        <template v-else>
          <button
            v-for="article in articles"
            :key="article.doc_id"
            type="button"
            class="help-list-item"
            :class="{ active: selectedDocId === article.doc_id }"
            @click="selectArticle(article)"
          >
            <div class="help-item-title">{{ article.title }}</div>
            <div class="help-item-summary">{{ article.summary }}</div>
            <div class="help-item-meta">
              <span class="help-badge type">{{ article.article_type || 'faq' }}</span>
              <span v-if="article.tenant_scope === 'tenant'" class="help-badge tenant">tenant</span>
            </div>
          </button>
        </template>
      </div>

      <div class="help-content-pane">
        <div v-if="loadingArticle" class="help-empty-state">Loading article...</div>
        <div v-else-if="!selectedArticle" class="help-empty-state">Select an article from the list.</div>
        <template v-else>
          <div class="help-article-body">
            <div class="help-content" v-html="renderContent(articleContent)"></div>
            <div class="help-feedback">
              <span class="mr-2">Was this helpful?</span>
              <b-button
                size="sm"
                :variant="feedbackValue === true ? 'success' : 'outline-success'"
                @click="submitFeedback(true)"
                :disabled="feedbackSubmitting"
              >
                Yes
              </b-button>
              <b-button
                size="sm"
                :variant="feedbackValue === false ? 'danger' : 'outline-danger'"
                class="ml-2"
                @click="submitFeedback(false)"
                :disabled="feedbackSubmitting"
              >
                No
              </b-button>
            </div>
            <div v-if="feedbackValue !== null && !feedbackCommentSubmitted" class="help-feedback-comment">
              <label class="help-feedback-comment-label">Anything to add? (optional)</label>
              <b-form-textarea
                v-model="feedbackComment"
                placeholder="e.g. What could we improve?"
                rows="2"
                max-rows="4"
                size="sm"
                class="help-feedback-comment-input"
              ></b-form-textarea>
              <b-button size="sm" variant="secondary" @click="submitComment" :disabled="feedbackSubmitting">
                Submit comment
              </b-button>
            </div>
            <div v-else-if="feedbackCommentSubmitted" class="help-feedback-comment-done">
              Thanks for your feedback.
            </div>

            <div v-if="isAdmin && feedbackSummary && feedbackSummary.total > 0" class="help-feedback-admin">
              <div class="help-feedback-summary">
                <span class="fb-stat yes">{{ feedbackSummary.yes_count }} Yes</span>
                <span class="fb-stat no">{{ feedbackSummary.no_count }} No</span>
                <span class="fb-stat pct">({{ feedbackSummary.helpful_pct }}% helpful)</span>
                <a v-if="isAdmin" href="#" class="fb-toggle" @click.prevent="feedbackDetailExpanded = !feedbackDetailExpanded">
                  {{ feedbackDetailExpanded ? 'Hide details' : 'Show details' }}
                </a>
              </div>
              <div v-if="feedbackDetailExpanded" class="help-feedback-detail">
                <table class="fb-detail-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Vote</th>
                      <th>Comment</th>
                      <th>Date</th>
                      <th>Page</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(entry, idx) in feedbackSummary.entries" :key="idx">
                      <td>{{ entry.user_id }}</td>
                      <td>
                        <span :class="entry.helpful ? 'fb-vote-yes' : 'fb-vote-no'">{{ entry.helpful ? 'Yes' : 'No' }}</span>
                      </td>
                      <td class="fb-comment-cell">{{ entry.comment || '—' }}</td>
                      <td>{{ formatFeedbackDate(entry.created_at) }}</td>
                      <td>{{ entry.route_name || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- COMPANY DOCUMENTS TAB -->
    <div v-if="activeTab === 'company'" class="help-body">
      <div class="help-sidebar">
        <div v-if="loadingCompanyDocs" class="help-empty-state">
          Loading documents...
        </div>
        <div v-else-if="companyDocs.length === 0" class="help-empty-state">
          No company documents available.
        </div>
        <template v-else>
          <button
            v-for="doc in companyDocs"
            :key="doc.doc_id"
            type="button"
            class="help-list-item"
            :class="{ active: selectedCompanyDocId === doc.doc_id }"
            @click="selectCompanyDoc(doc)"
          >
            <div class="help-item-title">{{ doc.display_name }}</div>
            <div v-if="doc.purpose" class="help-item-summary">{{ doc.purpose }}</div>
            <div class="help-item-meta">
              <span v-if="doc.doc_type" class="help-badge type">{{ doc.doc_type }}</span>
              <span v-if="doc.audience" class="help-badge tenant">{{ doc.audience }}</span>
            </div>
          </button>
        </template>
      </div>

      <div class="help-content-pane">
        <div v-if="loadingCompanyDoc" class="help-empty-state">Loading document...</div>
        <div v-else-if="!selectedCompanyDoc" class="help-empty-state">Select a document from the list.</div>
        <template v-else>
          <div class="help-article-body">
            <div class="company-doc-header">
              <h2 class="company-doc-title">{{ selectedCompanyDoc.display_name }}</h2>
              <div class="company-doc-meta">
                <span v-if="selectedCompanyDoc.doc_type" class="help-badge type">{{ selectedCompanyDoc.doc_type }}</span>
                <span v-if="selectedCompanyDoc.audience" class="help-badge tenant">{{ selectedCompanyDoc.audience }}</span>
                <span v-if="selectedCompanyDoc.uploaded_at" class="company-doc-date">
                  Uploaded {{ formatUploadDate(selectedCompanyDoc.uploaded_at) }}
                </span>
              </div>
              <div v-if="selectedCompanyDoc.purpose" class="company-doc-purpose">{{ selectedCompanyDoc.purpose }}</div>
            </div>
            <pre class="company-doc-text">{{ companyDocContent }}</pre>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { list_help_articles, get_help_article, submit_help_feedback, get_help_feedback, list_help_company_documents, get_help_company_document } from '../networking/apis'
import { tbn_list_help_articles, tbn_get_help_article, tbn_submit_help_feedback, tbn_get_help_feedback, tbn_list_help_company_documents, tbn_get_help_company_document } from '../networking/tbn_apis'
import { renderMarkdownToHtml } from '../utils/markdown_renderer'

export default {
  name: 'help_center',
  data() {
    return {
      activeTab: 'articles',
      loadingArticles: false,
      loadingArticle: false,
      feedbackSubmitting: false,
      feedbackValue: null,
      feedbackComment: '',
      feedbackCommentSubmitted: false,
      feedbackSummary: null,
      feedbackDetailExpanded: false,
      searchText: '',
      activeSearch: '',
      articles: [],
      selectedArticle: null,
      selectedDocId: null,
      articleContent: '',
      loadingCompanyDocs: false,
      loadingCompanyDoc: false,
      companyDocs: [],
      selectedCompanyDoc: null,
      selectedCompanyDocId: null,
      companyDocContent: '',
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant_type() {
      return this.user.types && this.user.types[this.user.tenant] ? this.user.types[this.user.tenant] : 'CM'
    },
    is_tbn() {
      return this.tenant_type === 'TBN'
    },
    enabledFeatures() {
      const featureMap = this.user && this.user.features && this.user.features[this.user.tenant]
        ? this.user.features[this.user.tenant]
        : {}
      return Object.keys(featureMap).filter(k => featureMap[k])
    },
    isAdmin() {
      return this.user.role === 'admin'
    },
  },
  watch: {
    '$route'(to) {
      const id = (to.params && to.params.articleId) || null
      if (id === this.selectedDocId) return
      this.selectedDocId = id
      if (!id) {
        this.selectedArticle = null
        this.articleContent = ''
        this.feedbackSummary = null
        return
      }
      const inList = this.articles.find(a => a.doc_id === id)
      if (inList) {
        this.selectArticle(inList)
      } else if (this.articles.length > 0) {
        this.loadArticleById(id)
      }
    },
  },
  created() {
    this.selectedDocId = (this.$route.params && this.$route.params.articleId) || null
    this.loadArticles()
  },
  methods: {
    async loadArticles() {
      this.loadingArticles = true
      this.activeSearch = (this.searchText || '').trim()
      try {
        const params = {
          route_name: this.$route && this.$route.name ? this.$route.name : '',
          query: this.searchText || '',
          enabled_features: this.enabledFeatures,
          limit: 100,
        }
        const fn = this.is_tbn ? tbn_list_help_articles : list_help_articles
        const { value: { data = {} } } = await fn(this.user.tenant, params)
        const payload = data.data || data
        this.articles = payload.articles || []
        if (this.articles.length > 0) {
          const selected = this.articles.find(a => a.doc_id === this.selectedDocId) || this.articles[0]
          if (this.selectedDocId && !this.articles.find(a => a.doc_id === this.selectedDocId)) {
            await this.loadArticleById(this.selectedDocId)
          } else {
            await this.selectArticle(selected)
          }
        } else {
          if (this.selectedDocId) {
            await this.loadArticleById(this.selectedDocId)
          } else {
            this.selectedArticle = null
            this.selectedDocId = null
            this.articleContent = ''
            this.$router.replace({ name: 'help_center' }).catch(() => {})
          }
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to load help articles', 'danger', 5000)
      } finally {
        this.loadingArticles = false
      }
    },
    async selectArticle(article) {
      if (!article || !article.doc_id) return
      this.selectedArticle = article
      this.selectedDocId = article.doc_id
      this.$router.replace({ name: 'help_center', params: { articleId: article.doc_id } }).catch(() => {})
      this.feedbackValue = null
      this.feedbackComment = ''
      this.feedbackCommentSubmitted = false
      this.feedbackSummary = null
      this.feedbackDetailExpanded = false
      this.loadingArticle = true
      try {
        const fn = this.is_tbn ? tbn_get_help_article : get_help_article
        const { value: { data = {} } } = await fn(this.user.tenant, article.doc_id)
        const payload = data.data || data
        this.articleContent = payload.content || ''
        if (payload.title || payload.summary != null) {
          this.selectedArticle = { ...article, title: payload.title || article.title, summary: payload.summary != null ? payload.summary : article.summary }
        }
        if (payload.user_feedback) {
          this.feedbackValue = payload.user_feedback.helpful
          this.feedbackCommentSubmitted = !!(payload.user_feedback.comment && payload.user_feedback.comment.trim())
        }
      } catch (e) {
        this.articleContent = ''
        this.$emit('showAlert', 'Unable to load help article content', 'danger', 5000)
      } finally {
        this.loadingArticle = false
      }
      if (this.isAdmin && article.doc_id) {
        this.loadFeedback(article.doc_id)
      }
    },
    async loadArticleById(docId) {
      if (!docId) return
      this.loadingArticle = true
      this.selectedArticle = { doc_id: docId, title: 'Article', summary: '' }
      this.selectedDocId = docId
      this.feedbackValue = null
      this.feedbackComment = ''
      this.feedbackCommentSubmitted = false
      this.feedbackSummary = null
      try {
        const fn = this.is_tbn ? tbn_get_help_article : get_help_article
        const { value: { data = {} } } = await fn(this.user.tenant, docId)
        const payload = data.data || data
        this.articleContent = payload.content || ''
        if (payload.title) this.selectedArticle = { ...this.selectedArticle, title: payload.title }
        if (payload.summary != null) this.selectedArticle = { ...this.selectedArticle, summary: payload.summary || '' }
        if (payload.user_feedback) {
          this.feedbackValue = payload.user_feedback.helpful
          this.feedbackCommentSubmitted = !!(payload.user_feedback.comment && payload.user_feedback.comment.trim())
        }
        this.$router.replace({ name: 'help_center', params: { articleId: docId } }).catch(() => {})
      } catch (e) {
        this.articleContent = ''
        this.selectedArticle = null
        this.selectedDocId = null
        this.$emit('showAlert', 'Help article not found', 'warning', 4000)
        this.$router.replace({ name: 'help_center' }).catch(() => {})
      } finally {
        this.loadingArticle = false
      }
      if (this.isAdmin && docId) {
        this.loadFeedback(docId)
      }
    },
    async loadFeedback(docId) {
      try {
        const fn = this.is_tbn ? tbn_get_help_feedback : get_help_feedback
        const { value: { data = {} } } = await fn(this.user.tenant, docId)
        this.feedbackSummary = data.data || data
      } catch (e) {
        this.feedbackSummary = null
      }
    },
    async submitFeedback(helpful, comment) {
      if (!this.selectedDocId) return
      const commentText = typeof comment === 'string' ? comment.trim() : ''
      this.feedbackSubmitting = true
      try {
        const fn = this.is_tbn ? tbn_submit_help_feedback : submit_help_feedback
        const body = {
          doc_id: this.selectedDocId,
          helpful: !!helpful,
          route_name: this.$route && this.$route.name ? this.$route.name : '',
        }
        if (commentText) body.comment = commentText
        const { value: { data = {} } } = await fn(this.user.tenant, body)
        this.feedbackValue = helpful
        if (typeof comment === 'string') {
          this.feedbackComment = ''
          this.feedbackCommentSubmitted = true
        }
        const payload = data.data || data
        if (payload && payload.recorded) {
          this.feedbackSummary = payload
        } else if (this.isAdmin && this.selectedDocId) {
          this.loadFeedback(this.selectedDocId)
        }
      } catch (e) {
        this.$emit('showAlert', 'Could not submit feedback', 'warning', 4000)
      } finally {
        this.feedbackSubmitting = false
      }
    },
    submitComment() {
      this.submitFeedback(this.feedbackValue, this.feedbackComment)
    },
    switchTab(tab) {
      if (this.activeTab === tab) return
      this.activeTab = tab
      if (tab === 'company' && this.companyDocs.length === 0 && !this.loadingCompanyDocs) {
        this.loadCompanyDocs()
      }
    },
    async loadCompanyDocs() {
      this.loadingCompanyDocs = true
      try {
        const fn = this.is_tbn ? tbn_list_help_company_documents : list_help_company_documents
        const { value: { data = {} } } = await fn(this.user.tenant)
        const payload = data.data || data
        this.companyDocs = payload.documents || []
        if (this.companyDocs.length > 0 && !this.selectedCompanyDocId) {
          await this.selectCompanyDoc(this.companyDocs[0])
        }
      } catch (e) {
        this.$emit('showAlert', 'Unable to load company documents', 'danger', 5000)
      } finally {
        this.loadingCompanyDocs = false
      }
    },
    async selectCompanyDoc(doc) {
      if (!doc || !doc.doc_id) return
      this.selectedCompanyDoc = doc
      this.selectedCompanyDocId = doc.doc_id
      this.loadingCompanyDoc = true
      try {
        const fn = this.is_tbn ? tbn_get_help_company_document : get_help_company_document
        const { value: { data = {} } } = await fn(this.user.tenant, doc.doc_id)
        const payload = data.data || data
        this.companyDocContent = payload.content || ''
      } catch (e) {
        this.companyDocContent = ''
        this.$emit('showAlert', 'Unable to load document content', 'danger', 5000)
      } finally {
        this.loadingCompanyDoc = false
      }
    },
    formatUploadDate(epoch) {
      if (!epoch) return ''
      try {
        return new Date(epoch * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      } catch (e) {
        return ''
      }
    },
    clearSearch() {
      this.searchText = ''
      this.activeSearch = ''
      this.loadArticles()
    },
    formatFeedbackDate(epochSeconds) {
      if (!epochSeconds) return '-'
      try {
        return new Date(epochSeconds * 1000).toLocaleString(undefined, {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        })
      } catch (e) {
        return '-'
      }
    },
    renderContent(content) {
      return renderMarkdownToHtml(content)
    },
  },
}
</script>

<style scoped>
.help-center-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  overflow: hidden;
  text-align: left;
}

.help-tabs {
  display: flex;
  justify-content: center;
  gap: 2px;
  margin-bottom: 4px;
}

.help-tab {
  padding: 5px 18px;
  border: 1px solid #ccc;
  border-radius: 4px 4px 0 0;
  background: #ddd;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.help-tab:hover {
  background: #e8e8e8;
}

.help-tab.active {
  background: #fff;
  color: #1a5ea8;
  border-bottom-color: #fff;
}

.help-search {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.help-search .form-control {
  width: 220px;
}

.help-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.help-sidebar {
  width: 320px;
  min-width: 280px;
  border-right: 1px solid #dce1e6;
  background: #fbfbfc;
  overflow-y: auto;
  padding: 10px;
  flex-shrink: 0;
}

.help-list-item {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: #fff;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}

.help-list-item:hover {
  background: #f0f4f8;
  border-color: #c8d6e0;
}

.help-list-item.active {
  background: #e8f0fe;
  border-color: #4a90d9;
}

.help-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 3px;
  line-height: 1.3;
}

.help-list-item.active .help-item-title {
  color: #1a5ea8;
}

.help-item-summary {
  font-size: 11.5px;
  color: #777;
  line-height: 1.35;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.help-item-meta {
  display: flex;
  gap: 4px;
}

.help-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.help-badge.type {
  background: #eef0f2;
  color: #5a6268;
}

.help-badge.tenant {
  background: #dbeafe;
  color: #1e5fa8;
}

.help-content-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  text-align: left;
}

.help-article-header {
  padding: 16px 24px 0;
  flex-shrink: 0;
}

.help-article-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px;
}

.help-article-summary {
  font-size: 13px;
  color: #6c757d;
  margin: 0 0 12px;
  line-height: 1.4;
}

.help-article-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 16px;
}

.help-content {
  white-space: normal;
  line-height: 1.6;
  font-size: 14px;
  color: #333;
}

.help-feedback {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 13px;
  color: #666;
}

.help-feedback-comment {
  margin-top: 10px;
  font-size: 13px;
  color: #666;
}

.help-feedback-comment-label {
  display: block;
  margin-bottom: 4px;
}

.help-feedback-comment-input {
  max-width: 480px;
  margin-bottom: 6px;
}

.help-feedback-comment-done {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
  font-style: italic;
}

.help-feedback-admin {
  margin-top: 10px;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
}

.help-feedback-summary {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fb-stat {
  font-weight: 600;
}

.fb-stat.yes {
  color: #28a745;
}

.fb-stat.no {
  color: #dc3545;
}

.fb-stat.pct {
  color: #555;
  font-weight: 400;
}

.fb-toggle {
  font-size: 11px;
  color: #007bff;
  margin-left: auto;
}

.help-feedback-detail {
  margin-top: 8px;
}

.fb-detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.fb-detail-table th,
.fb-detail-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #e9ecef;
  text-align: left;
}

.fb-detail-table th {
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #dee2e6;
}

.fb-comment-cell {
  max-width: 240px;
  word-break: break-word;
}

.fb-vote-yes {
  color: #28a745;
  font-weight: 600;
}

.fb-vote-no {
  color: #dc3545;
  font-weight: 600;
}

.help-search-indicator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  margin-bottom: 6px;
  background: #e8f0fe;
  border: 1px solid #b8d4f0;
  border-radius: 5px;
  font-size: 12px;
  color: #1a5ea8;
}

.help-search-clear {
  border: none;
  background: transparent;
  font-size: 16px;
  line-height: 1;
  color: #1a5ea8;
  cursor: pointer;
  padding: 0 2px;
  font-weight: 600;
}

.help-search-clear:hover {
  color: #c00;
}

.help-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  color: #999;
  font-size: 14px;
}

.company-doc-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}

.company-doc-title {
  font-size: 1.2em;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 6px;
}

.company-doc-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.company-doc-date {
  font-size: 11px;
  color: #888;
}

.company-doc-purpose {
  margin-top: 6px;
  font-size: 13px;
  color: #555;
  font-style: italic;
}

.company-doc-text {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  color: #333;
  max-height: none;
  overflow: visible;
}
</style>

<style>
.help-content h1,
.help-content h2,
.help-content h3,
.help-content h4,
.help-content h5,
.help-content h6 {
  margin: 14px 0 6px;
  line-height: 1.3;
  font-weight: 600;
  color: #1a1a1a;
}

.help-content h1 {
  font-size: 1.3em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eee;
}

.help-content h2 {
  font-size: 1.15em;
}

.help-content h3 {
  font-size: 1.05em;
}

.help-content h4,
.help-content h5,
.help-content h6 {
  font-size: 1em;
}

.help-content p {
  margin: 0 0 8px;
}

.help-content ol,
.help-content ul {
  margin: 6px 0;
  padding-left: 22px;
}

.help-content li {
  margin: 3px 0;
}

.help-content code {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.9em;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  padding: 1px 5px;
}

.help-content pre {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.45;
  background: #f6f8fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  overflow-x: auto;
  margin: 8px 0;
}

.help-content pre code {
  background: transparent;
  border: none;
  padding: 0;
}

.help-content table.markdown-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
  font-size: 13px;
}

.help-content table.markdown-table th,
.help-content table.markdown-table td {
  border: 1px solid #dce1e6;
  padding: 7px 10px;
  vertical-align: top;
}

.help-content table.markdown-table th {
  background: #f5f7f9;
  font-weight: 600;
  color: #333;
}

.help-content table.markdown-table tr:nth-child(even) {
  background: #fafbfc;
}

.help-content strong {
  font-weight: 600;
}
</style>
